import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Field, FormSection } from '@/components/catalog/CatalogParts';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import {
  useDeleteSoftware,
  useSaveSoftware,
  type SoftwareInput,
  type SoftwareSubscription,
} from '@/features/software/hooks';
import {
  BILLING_CYCLES,
  BILLING_CYCLE_LABELS,
  SOFTWARE_CATEGORIES,
  SOFTWARE_CATEGORY_LABELS,
  SOFTWARE_STATUSES,
  SOFTWARE_STATUS_LABELS,
  friendlySoftwareError,
  softwareCosts,
  type SeatProfile,
} from '@/features/software/model';
import { centsToInputDollars, formatCurrency, inputDollarsToCents } from '@/lib/formatters';
import type { SoftwareBillingCycle, SoftwareCategory, SoftwareStatus } from '@/types/database';

const NO_OWNER = '__none';
const ANNUAL_UNKNOWN = 'unknown';

const PRICE_LABEL: Record<SoftwareBillingCycle, string> = {
  monthly: 'per month',
  quarterly: 'per quarter',
  annual: 'per year',
  one_time: 'one-time',
};

function toInput(sub: SoftwareSubscription | null): SoftwareInput {
  return {
    id: sub?.id,
    name: sub?.name ?? '',
    vendor: sub?.vendor ?? '',
    category: sub?.category ?? 'other',
    status: sub?.status ?? 'active',
    planName: sub?.plan_name ?? '',
    website: sub?.website ?? '',
    accountLogin: sub?.account_login ?? '',
    ownerId: sub?.owner_id ?? null,
    billingCycle: sub?.billing_cycle ?? 'monthly',
    price: centsToInputDollars(sub?.price_cents),
    pricePerSeat: sub?.price_per_seat ?? false,
    seatCount: sub?.seat_count != null ? String(sub.seat_count) : '',
    annualOptionAvailable: sub?.annual_option_available ?? null,
    annualPrice: centsToInputDollars(sub?.annual_price_cents),
    startedOn: sub?.started_on ?? '',
    renewsOn: sub?.renews_on ?? '',
    autoRenews: sub?.auto_renews ?? true,
    paymentMethod: sub?.payment_method ?? '',
    includes: sub?.includes ?? '',
    notes: sub?.notes ?? '',
  };
}

function safeCents(value: string): number | null {
  try {
    return inputDollarsToCents(value);
  } catch {
    return null;
  }
}

export function SoftwareDialog({
  open,
  subscription,
  assignedSeats,
  profiles,
  canEdit,
  onOpenChange,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  subscription: SoftwareSubscription | null;
  assignedSeats: number;
  profiles: SeatProfile[];
  canEdit: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (id: string, created: boolean) => void;
  onDeleted?: () => void;
}) {
  const save = useSaveSoftware();
  const remove = useDeleteSoftware();
  const [draft, setDraft] = useState<SoftwareInput>(() => toInput(subscription));
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(toInput(subscription));
    setConfirmDelete(false);
  }, [open, subscription]);

  const set = <K extends keyof SoftwareInput>(key: K, value: SoftwareInput[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const canSwitchToAnnual = draft.billingCycle === 'monthly' || draft.billingCycle === 'quarterly';
  const seatCountNumber = Number.parseInt(draft.seatCount, 10);
  const preview = useMemo(
    () =>
      softwareCosts(
        {
          billing_cycle: draft.billingCycle,
          price_cents: safeCents(draft.price),
          price_per_seat: draft.pricePerSeat,
          seat_count:
            Number.isFinite(seatCountNumber) && seatCountNumber > 0 ? seatCountNumber : null,
          annual_option_available: draft.annualOptionAvailable,
          annual_price_cents: safeCents(draft.annualPrice),
        },
        assignedSeats,
      ),
    [draft, seatCountNumber, assignedSeats],
  );

  const pending = save.isPending || remove.isPending;
  const priceSuffix = draft.pricePerSeat
    ? `Price per seat, ${PRICE_LABEL[draft.billingCycle]}`
    : `Price ${PRICE_LABEL[draft.billingCycle]}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit) return;
            void save
              .mutateAsync(draft)
              .then((id) => {
                toast.success(subscription ? 'Software updated.' : 'Software added.');
                onOpenChange(false);
                onSaved?.(id, !subscription);
              })
              .catch((err) => toast.error(friendlySoftwareError(err, 'Could not save.')));
          }}
        >
          <DialogHeader>
            <DialogTitle>{subscription ? `Edit ${subscription.name}` : 'Add software'}</DialogTitle>
            <DialogDescription>
              Track what the subscription costs, what it includes, and who uses it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <FormSection title="Basics">
              <Field label="Name" htmlFor="software-name" className="sm:col-span-2">
                <Input
                  id="software-name"
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. SolidWorks"
                  disabled={!canEdit}
                  required
                />
              </Field>
              <Field label="Vendor" htmlFor="software-vendor">
                <Input
                  id="software-vendor"
                  value={draft.vendor}
                  onChange={(e) => set('vendor', e.target.value)}
                  placeholder="e.g. Dassault Systèmes"
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Plan" htmlFor="software-plan">
                <Input
                  id="software-plan"
                  value={draft.planName}
                  onChange={(e) => set('planName', e.target.value)}
                  placeholder="e.g. Professional"
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Category">
                <Select
                  value={draft.category}
                  onValueChange={(value) => set('category', value as SoftwareCategory)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOFTWARE_CATEGORIES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {SOFTWARE_CATEGORY_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select
                  value={draft.status}
                  onValueChange={(value) => set('status', value as SoftwareStatus)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOFTWARE_STATUSES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {SOFTWARE_STATUS_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </FormSection>

            <FormSection title="Cost">
              <Field label="Billing">
                <Select
                  value={draft.billingCycle}
                  onValueChange={(value) => set('billingCycle', value as SoftwareBillingCycle)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_CYCLES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {BILLING_CYCLE_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={priceSuffix} htmlFor="software-price">
                <Input
                  id="software-price"
                  inputMode="decimal"
                  value={draft.price}
                  onChange={(e) => set('price', e.target.value)}
                  placeholder="0.00"
                  disabled={!canEdit}
                />
              </Field>
              <Field
                label="Seats or licenses"
                htmlFor="software-seats"
                hint={
                  draft.pricePerSeat
                    ? `Leave blank to bill for the ${assignedSeats} assigned ${assignedSeats === 1 ? 'seat' : 'seats'}.`
                    : 'How many the plan includes. Optional.'
                }
              >
                <Input
                  id="software-seats"
                  inputMode="numeric"
                  value={draft.seatCount}
                  onChange={(e) => set('seatCount', e.target.value)}
                  placeholder={draft.pricePerSeat ? String(assignedSeats) : '—'}
                  disabled={!canEdit}
                />
              </Field>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={draft.pricePerSeat}
                    onCheckedChange={(checked) => set('pricePerSeat', checked)}
                    disabled={!canEdit}
                    aria-label="Priced per seat"
                  />
                  Priced per seat
                </label>
              </div>
              {preview.monthlyCents != null && (
                <p className="text-sm text-muted-foreground sm:col-span-2">
                  Comes to{' '}
                  <span className="font-medium text-foreground">
                    {formatCurrency(preview.monthlyCents)} a month
                  </span>{' '}
                  ({formatCurrency(preview.yearlyCents)} a year).
                </p>
              )}
            </FormSection>

            <FormSection title="Annual plan">
              {canSwitchToAnnual ? (
                <>
                  <Field label="Is there an annual plan?">
                    <Select
                      value={
                        draft.annualOptionAvailable == null
                          ? ANNUAL_UNKNOWN
                          : draft.annualOptionAvailable
                            ? 'yes'
                            : 'no'
                      }
                      onValueChange={(value) =>
                        set(
                          'annualOptionAvailable',
                          value === ANNUAL_UNKNOWN ? null : value === 'yes',
                        )
                      }
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ANNUAL_UNKNOWN}>Haven’t checked</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {draft.annualOptionAvailable === true && (
                    <Field
                      label={
                        draft.pricePerSeat
                          ? 'Annual price per seat, per year'
                          : 'Annual price per year'
                      }
                      htmlFor="software-annual-price"
                    >
                      <Input
                        id="software-annual-price"
                        inputMode="decimal"
                        value={draft.annualPrice}
                        onChange={(e) => set('annualPrice', e.target.value)}
                        placeholder="0.00"
                        disabled={!canEdit}
                      />
                    </Field>
                  )}
                  {preview.annualSavingsCents != null && (
                    <p className="text-sm text-muted-foreground sm:col-span-2">
                      {preview.annualSavingsCents > 0 ? (
                        <>
                          Switching to annual would save{' '}
                          <span className="font-medium text-bright-moss-dark">
                            {formatCurrency(preview.annualSavingsCents)} a year
                          </span>
                          .
                        </>
                      ) : preview.annualSavingsCents < 0 ? (
                        <>
                          The annual plan costs{' '}
                          {formatCurrency(Math.abs(preview.annualSavingsCents))} more a year.
                        </>
                      ) : (
                        'The annual plan costs the same.'
                      )}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground sm:col-span-2">
                  {draft.billingCycle === 'annual'
                    ? 'Already billed annually.'
                    : 'One-time purchases don’t have an annual plan.'}
                </p>
              )}
            </FormSection>

            <FormSection title="Dates & payment">
              <Field label="Started" htmlFor="software-started">
                <Input
                  id="software-started"
                  type="date"
                  value={draft.startedOn}
                  onChange={(e) => set('startedOn', e.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Next renewal" htmlFor="software-renews">
                <Input
                  id="software-renews"
                  type="date"
                  value={draft.renewsOn}
                  onChange={(e) => set('renewsOn', e.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field
                label="Paid with"
                htmlFor="software-payment"
                hint="Card or account, e.g. “Mercury card ending 4821”."
              >
                <Input
                  id="software-payment"
                  value={draft.paymentMethod}
                  onChange={(e) => set('paymentMethod', e.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={draft.autoRenews}
                    onCheckedChange={(checked) => set('autoRenews', checked)}
                    disabled={!canEdit}
                    aria-label="Auto-renews"
                  />
                  Auto-renews
                </label>
              </div>
            </FormSection>

            <FormSection title="Account">
              <Field
                label="Account login"
                htmlFor="software-login"
                hint="Email or username the account is registered to. Passwords go on the account’s page under Software → Accounts."
              >
                <Input
                  id="software-login"
                  value={draft.accountLogin}
                  onChange={(e) => set('accountLogin', e.target.value)}
                  placeholder="e.g. design@virro.com"
                  autoComplete="off"
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Website or sign-in page" htmlFor="software-website">
                <Input
                  id="software-website"
                  value={draft.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="https://"
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Owner" hint="Who manages the account and its billing.">
                <Select
                  value={draft.ownerId ?? NO_OWNER}
                  onValueChange={(value) => set('ownerId', value === NO_OWNER ? null : value)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_OWNER}>No owner</SelectItem>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name || p.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </FormSection>

            <FormSection title="What we get">
              <Field
                label="Included in the plan"
                htmlFor="software-includes"
                className="sm:col-span-2"
              >
                <Textarea
                  id="software-includes"
                  value={draft.includes}
                  onChange={(e) => set('includes', e.target.value)}
                  placeholder="Features, storage, add-ons, support level…"
                  rows={3}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Notes" htmlFor="software-notes" className="sm:col-span-2">
                <Textarea
                  id="software-notes"
                  value={draft.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  disabled={!canEdit}
                />
              </Field>
            </FormSection>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {canEdit && subscription ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync(subscription.id)
                      .then(() => {
                        toast.success(`${subscription.name} deleted.`);
                        onOpenChange(false);
                        onDeleted?.();
                      })
                      .catch((err) => toast.error(friendlySoftwareError(err, 'Could not delete.')))
                  }
                >
                  Confirm delete
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {canEdit && (
                <Button type="submit" disabled={pending || !draft.name.trim()}>
                  {subscription ? 'Save' : 'Add software'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}